import { Injectable, Signal, computed, signal } from '@angular/core'

/**
 * Registro central de "hay trabajo sin guardar en pantalla en este momento".
 *
 * Motivo: la app se recarga sola en un par de situaciones (nueva version
 * desplegada, Service Worker roto — ver app.ts). Antes de forzar ese reload,
 * necesitamos saber si alguien tiene fotos tomadas o campos llenos sin
 * guardar en `captura-producto`/`nuevo-proveedor` — todo eso vive solo en
 * memoria (signals) hasta que se guarda explicitamente, asi que un reload
 * en mal momento lo borra sin recuperacion posible.
 *
 * Cada pantalla que tenga un "borrador" se registra aca con su propio
 * criterio (un Signal<boolean> que ella misma arma); este servicio no sabe
 * ni le importa que campos son — solo agrega. Quien se registra debe
 * desregistrarse al destruirse (ngOnDestroy) para no dejar una fuente
 * fantasma bloqueando reloads para siempre.
 */
@Injectable({ providedIn: 'root' })
export class BloqueoRecargaService {
  private readonly fuentes = new Map<string, Signal<boolean>>()
  // No hay forma de "reaccionar" a que el Map cambie de tamaño; esta cuenta
  // fuerza que `hayTrabajoSinGuardar` se recalcule cuando se registra/quita
  // una fuente, ademas de cuando cambia el valor de alguna ya registrada.
  private readonly version = signal(0)

  registrar(id: string, activo: Signal<boolean>): void {
    this.fuentes.set(id, activo)
    this.version.update((v) => v + 1)
  }

  desregistrar(id: string): void {
    this.fuentes.delete(id)
    this.version.update((v) => v + 1)
  }

  readonly hayTrabajoSinGuardar = computed(() => {
    this.version()
    for (const activo of this.fuentes.values()) {
      if (activo()) return true
    }
    return false
  })
}
